import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Search, CheckCircle2, ChefHat, Clock, PackageCheck, AlertCircle, ArrowLeft } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { useNavigate, useSearchParams } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TrackOrder() {
  const [orderNumber, setOrderNumber] = useState('');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();


  useEffect(() => {
    const orderParam = searchParams.get('order');
    if (!orderParam) return;
    setOrderNumber(orderParam);
  }, [searchParams]);

  useEffect(() => {
    if (!orderNumber) return;
    const orderParam = searchParams.get('order');
    if (!orderParam) return;
    const run = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await axios.get(`${API}/orders/track/${orderNumber}`);
        setOrder(response.data);
      } catch {
        setError('Order not found. Please check the number.');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [orderNumber, searchParams]);

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!orderNumber) return;
    
    setLoading(true);
    setError('');
    setOrder(null);

    try {
      const response = await axios.get(`${API}/orders/track/${orderNumber}`);
      setOrder(response.data);
    } catch (err) {
      setError('Order not found. Please check the number.');
    } finally {
      setLoading(false);
    }
  };

  // Helper: Determine if a step is completed, current, or pending
  const getStepStatus = (stepStatus, currentStatus) => {
    const sequence = ['new', 'cooking', 'ready', 'completed'];
    const currentIndex = sequence.indexOf(currentStatus);
    const stepIndex = sequence.indexOf(stepStatus);

    if (currentStatus === 'cancelled') return 'error';
    if (stepIndex < currentIndex) return 'completed';
    if (stepIndex === currentIndex) return 'current';
    return 'pending';
  };

  const steps = [
    { key: 'new', label: 'Placed', icon: Clock },
    { key: 'cooking', label: 'Cooking', icon: ChefHat },
    { key: 'ready', label: 'Ready', icon: CheckCircle2 },
    { key: 'completed', label: 'Received', icon: PackageCheck },
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg shadow-lg">
        <CardHeader className="text-center relative">
          <Button 
            variant="ghost" 
            size="icon" 
            className="absolute left-4 top-4"
            onClick={() => navigate('/')}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <CardTitle className="text-2xl">Track Order</CardTitle>
          <CardDescription>Enter your order number (e.g., 1001)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          
          {/* Search Input */}
          <form onSubmit={handleTrack} className="flex gap-2">
            <Input
              placeholder="Order Number"
              type="number"
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)}
              className="text-lg"
              autoFocus
            />
            <Button type="submit" disabled={loading}>
              {loading ? <Clock className="animate-spin w-4 h-4" /> : <Search className="w-4 h-4" />}
            </Button>
          </form>

          {error && (
            <div className="p-3 bg-red-100 text-red-700 rounded-lg flex items-center gap-2 text-sm">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          {/* Status Display */}
          {order && (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="text-center mb-8">
                 <h3 className="text-2xl font-bold mb-1">Order #{order.order_number}</h3>
                 <p className="text-slate-500">
                   {order.items.length} items • ₹{order.total}
                 </p>
                 {order.status === 'cancelled' && (
                   <Badge variant="destructive" className="mt-2 text-lg">Cancelled</Badge>
                 )}
              </div>

              {/* Progress Bar */}
              <div className="relative flex justify-between items-start w-full px-2">
                {/* Gray Background Line */}
                <div className="absolute top-4 left-0 w-full h-1 bg-slate-200 -z-0 rounded" />
                
                {/* Colored Progress Line */}
                <div 
                   className="absolute top-4 left-0 h-1 bg-green-500 transition-all duration-500 rounded -z-0"
                   style={{ 
                     width: order.status === 'completed' ? '100%' : 
                            order.status === 'ready' ? '75%' : 
                            order.status === 'cooking' ? '50%' : 
                            order.status === 'new' ? '25%' : '0%' 
                   }}
                />

                {/* Steps */}
                {steps.map((step) => {
                   const status = getStepStatus(step.key, order.status);
                   const isCompleted = status === 'completed';
                   const isCurrent = status === 'current';
                   const Icon = step.icon;

                   return (
                     <div key={step.key} className="flex flex-col items-center gap-2 z-10 bg-slate-50 dark:bg-slate-900 px-1">
                       <div className={`
                         w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 border-2
                         ${isCompleted || isCurrent ? 'bg-green-500 border-green-500 text-white' : 'bg-white border-slate-300 text-slate-300'}
                         ${isCurrent ? 'ring-4 ring-green-100 scale-110' : ''}
                       `}>
                         <Icon className="w-5 h-5" />
                       </div>
                       <span className={`text-xs font-medium ${isCompleted || isCurrent ? 'text-slate-900 font-bold' : 'text-slate-400'}`}>
                         {step.label}
                       </span>
                     </div>
                   );
                })}
              </div>

              {/* Item List */}
              <div className="mt-8 pt-6 border-t">
                <p className="font-semibold text-sm mb-3 text-slate-500">Order Details</p>
                <div className="space-y-2 text-sm">
                  {order.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between">
                      <span>{item.quantity}x {item.title}</span>
                      <span className="font-medium">₹{item.price * item.quantity}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
